// native
import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';

// service
import { CustomStyleService } from '../../../core/services/custom-style.service';
import { StylesService } from '../../../core/services/styles.service';
import { UtilityService } from '../../../core/services/utility.service';
import { ToastService } from '../../../core/services/toast.service';

// model
import { StylesInfo, IStyle } from '../../../models';

@Component({
  selector: 'scs-custom-style',
  templateUrl: './custom-style.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomStyleComponent implements OnInit, OnDestroy {

  stylesInfoSubscription: Subscription;

  customStyles: IStyle[] = [];

  constructor(
    private stylesService: StylesService,
    private customStyleService: CustomStyleService,
    private utilityService: UtilityService,
    private toastService: ToastService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.stylesInfoSubscription = this.stylesService.stylesInfo$
      .subscribe((response: StylesInfo) => {
        if (response)
          this.customStyles = response.custom_styles.filter(style => style.id.indexOf('usr-') === 0);
      });
  }

  ngOnDestroy() {
    this.stylesInfoSubscription?.unsubscribe();
  }

  onClose() {
    this.customStyleService.isCustomStyleDialogOpen = false;
  }

  addCustomStyle(fileText?: string) {
    const parser = new DOMParser();

    const xmlDoc = parser.parseFromString(fileText, "text/xml");
    if (!xmlDoc) {
      this.toastService.openToast({
        text: 'Please upload a valid xml document.',
        type: 'error'
      });
      return;
    }

    const parserErrorNode = this.utilityService.getPropOrNull(xmlDoc, 'parsererror');
    if (parserErrorNode) {
      const errMessage = parserErrorNode.textContent.split('Below is a rendering')[0];
      this.toastService.openToast({
        text: `Please upload a valid .xml or .csl document. Error: ${errMessage}`,
        type: 'error'
      });
      return;
    }

    const titleNode = this.utilityService.getPropOrNull(xmlDoc, 'title');
    const citationNode = this.utilityService.getPropOrNull(xmlDoc, 'citation');

    if (!titleNode || !citationNode || !titleNode.textContent) {
      this.toastService.openToast({
        text: 'Please upload a valid style document.',
        type: 'error'
      });
      return;
    }

    this.customStyleService.addCustomStyle(fileText, titleNode.textContent).subscribe((response: StylesInfo) => {
      response.custom_styles?.forEach(style => {
        style.isFavorite = response.favorite_style_ids?.includes(style.id);
      });
      this.stylesService.stylesInfo$.next(response);
      this.changeDetectorRef.markForCheck();

      this.toastService.openToast({
        text: 'Style has been successfully uploaded.',
        type: 'success'
      });
    });
  }

  removeCustomStyle(styleId: string) {
    if (styleId === this.stylesService.getSelectedStyle()?.id) {
      this.toastService.openToast({
        text: `Can't delete style that is currently selected.`,
        type: 'error'
      });
      return;
    }

    this.customStyleService.removeCustomStyle(styleId).subscribe((response: StylesInfo) => {
      response.custom_styles?.forEach(style => {
        style.isFavorite = response.favorite_style_ids?.includes(style.id);
      });
      this.stylesService.setClientCustomFavoriteStyles(this.stylesService.clientCustomFavoritesStyles.filter(style => style.id !== styleId));
      this.stylesService.stylesInfo$.next(response);
      this.changeDetectorRef.markForCheck();

      this.toastService.openToast({
        text: 'Style has been successfully removed.',
        type: 'success'
      });
    });
  }

  onOpenStyleEditor() {
    window.open(
      'https://editor.citationstyles.org/visualEditor',
      '_blank'
    );
  }
}
